import { Button, Form } from 'antd';
import { values as objValues, pick } from 'lodash-es';
import { useContext, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { FAIcon, FATypes } from '../../components/adapters/fontAwesomeAdapters';
import { FiltersAndSortContext } from '../../components/data/FiltersAndSortProvider';
import { FormItemInputText } from '../../components/forms/basicFormElements';
import { FormItem } from '../../components/forms/FormItem';
import { isFormSectionNonEmpty } from '../../components/forms/formHelpers';
import { VerticalForm } from '../../components/forms/forms';
import { FormItemSelect } from '../../components/forms/selects';
import { MonitorFilterType } from './MonitorFilter';
import {
  FilterPopup,
  MonitorFilter,
  useAirportOptions,
  useSimpleAccountOptions,
  useStatusOptions,
} from './MonitorFilterShared';
import { AdvancedFilterMultiSelect } from './StatusMultiSelect';
import {
  FilterSetsListPopup,
  SaveFilterSetPopup,
  useMonitorFilterTemplatesActiveItem,
} from './monitorFilterTemplates';
import { useAdvancedFiltersPopupStyles } from './AdvancedFiltersPopup.styles';

const ADVANCED_FIELDS = objValues(MonitorFilterType)
  .filter(def => def.isAdvanced)
  .map(def => def.name);

const emptyAdvancedValues = Object.fromEntries(
  ADVANCED_FIELDS.map(name => [name, undefined])
);

const SECTIONS = [
  [
    {
      name: 'jobNumber',
      path: ['shipmentDetails', 'jobNumber'],
      type: 'text',
      labelId: 'filters.labels.shipmentDetails.jobNumber',
    },
    {
      name: 'shipperReference',
      path: ['shipmentDetails', 'shipperReference'],
      type: 'text',
      labelId: 'filters.labels.shipmentDetails.shipperReference',
    },
    {
      name: 'shipmentStatus',
      path: 'shipmentStatus',
      type: 'checkboxList',
      optionsKey: 'status',
      labelId: 'filters.labels.status',
    },
    {
      name: 'costCenter',
      path: ['shipmentDetails', 'costCenter'],
      type: 'text',
      labelId: 'filters.labels.shipmentDetails.costCenter',
    },
  ],
  [
    {
      name: 'accountNumber',
      path: 'accountNumber',
      type: 'checkboxList',
      optionsKey: 'account',
      labelId: 'filters.labels.account',
    },
    {
      name: 'shipperName',
      path: ['shipmentDetails', 'shipperName'],
      type: 'text',
      labelId: 'filters.labels.shipmentDetails.shipperName',
    },
    {
      name: 'destinationContactName',
      path: ['shipmentDetails', 'destinationContactName'],
      type: 'text',
      labelId: 'filters.labels.shipmentDetails.destinationContactName',
    },
  ],
  [
    {
      name: 'shipperCountry',
      path: ['shipmentDetails', 'shipperCountry'],
      type: 'text',
      labelId: 'filters.labels.shipmentDetails.shipperCountry',
    },
    {
      name: 'destinationCountry',
      path: ['shipmentDetails', 'destinationCountry'],
      type: 'text',
      labelId: 'filters.labels.shipmentDetails.destinationCountry',
    },
    {
      name: 'shipperCity',
      path: ['shipmentDetails', 'shipperCity'],
      type: 'text',
      labelId: 'filters.labels.shipmentDetails.shipperCity',
    },
    {
      name: 'destinationCity',
      path: ['shipmentDetails', 'destinationCity'],
      type: 'text',
      labelId: 'filters.labels.shipmentDetails.destinationCity',
    },
    {
      name: 'originAirport',
      path: 'originAirport',
      type: 'checkboxList',
      optionsKey: 'airport',
      labelId: 'filters.labels.shipmentDetails.originAirport',
    },
    {
      name: 'destinationAirport',
      path: 'destinationAirport',
      type: 'checkboxList',
      optionsKey: 'airport',
      labelId: 'filters.labels.shipmentDetails.destinationAirport',
    },
  ],
];

function renderField(field, { optionsMap, formatMessage }) {
  const label = formatMessage({ id: field.labelId });
  const common = {
    key: field.name,
    name: field.path,
    floatingLabel: true,
    formItemComponentProps: { label },
  };
  if (field.type === 'select') {
    return (
      <FormItemSelect {...common} options={optionsMap[field.optionsKey]} />
    );
  }
  if (field.type === 'checkboxList') {
    return (
      <FormItem
        {...common}
        formItemComponentProps={{
          ...common.formItemComponentProps,
          isValuePresent: isFormSectionNonEmpty,
        }}
        component={AdvancedFilterMultiSelect}
        options={optionsMap[field.optionsKey]}
      />
    );
  }
  return <FormItemInputText {...common} />;
}

export const AdvancedFilters = props => (
  <MonitorFilter
    {...props}
    definition={{
      name: 'filters',
      filterComponent: AdvancedFiltersPopup,
      isClearable: false,
      isSortable: false,
    }}
  />
);

function AdvancedFiltersLabel() {
  const { styles } = useAdvancedFiltersPopupStyles();
  return (
    <span className={styles.activeLabel}>
      <FAIcon
        icon="filter"
        type={FATypes.SOLID}
        className={styles.filterPreviewIcon}
      />
      <span>
        <FormattedMessage id="filters.advancedFilters.title" />
      </span>
    </span>
  );
}

function AdvancedFiltersPopup() {
  const { formatMessage } = useIntl();
  const {
    filters: { values, setValues: setGlobalFilters, initialValues },
  } = useContext(FiltersAndSortContext);

  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const watchedValues = Form.useWatch([], form);

  const isPresent = useMemo(
    () => ADVANCED_FIELDS.some(field => isFormSectionNonEmpty(values[field])),
    [values]
  );

  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue({
        ...emptyAdvancedValues,
        ...pick(values, ADVANCED_FIELDS),
      });
    }
  }, [open, values, form]);

  const handleApply = () => {
    const localValues = form.getFieldsValue(ADVANCED_FIELDS);
    setGlobalFilters({
      ...values,
      ...localValues,
    });
    setOpen(false);
  };

  const handleClearAll = () => {
    form.resetFields();
    setGlobalFilters(initialValues);
    setOpen(false);
  };

  const { activeItem, setActiveItem, afterSave } =
    useMonitorFilterTemplatesActiveItem();

  const isFilterEmpty = ADVANCED_FIELDS.every(
    f => !isFormSectionNonEmpty(watchedValues?.[f])
  );

  const accountOptions = useSimpleAccountOptions();
  const statusOptions = useStatusOptions();
  const airportOptions = useAirportOptions();

  const optionsMap = useMemo(
    () => ({
      account: accountOptions,
      status: statusOptions,
      airport: airportOptions,
    }),
    [accountOptions, statusOptions, airportOptions]
  );

  const { styles } = useAdvancedFiltersPopupStyles();

  const popoverContent = (
    <VerticalForm form={form} className={styles.popupContent}>
      <div className={styles.popupContentHeader}>
        <div className={styles.popupContentTitle}>
          <FormattedMessage id="filters.advancedFilters.title" />
        </div>
        <FAIcon
          icon="times-circle"
          type={FATypes.SOLID}
          className={styles.popupContentClose}
          onClick={() => setOpen(false)}
        />
      </div>
      <div className={styles.filterSetsControls}>
        <FilterSetsListPopup
          activeItem={activeItem}
          setActiveItem={setActiveItem}
          customOpenIcon={<FAIcon icon="angle-right" className="icon-18" />}
          customClosedIcon={<FAIcon icon="angle-right" className="icon-18" />}
        />
        <SaveFilterSetPopup
          activeItem={activeItem}
          disabled={isFilterEmpty}
          afterSave={afterSave}
          localValues={pick(watchedValues, ADVANCED_FIELDS)}
        />
      </div>
      <div className={styles.advancedFiltersSections}>
        {SECTIONS.map(fields => (
          <div key={fields[0].name} className={styles.advancedFiltersSection}>
            {fields.map(field =>
              renderField(field, { optionsMap, formatMessage })
            )}
          </div>
        ))}
      </div>

      <div className={styles.popupContentFooter}>
        <a className={styles.clearAllLink} onClick={handleClearAll}>
          <FormattedMessage id="filters.buttons.clearAll" />
        </a>
        <div className={styles.actionButtons}>
          <Button onClick={() => setOpen(false)}>
            <FormattedMessage id="buttons.cancel" />
          </Button>
          <Button
            type="primary"
            onClick={handleApply}
            className={styles.applyButton}
          >
            <FormattedMessage id="buttons.apply" />
          </Button>
        </div>
      </div>
    </VerticalForm>
  );

  return (
    <FilterPopup
      label={<AdvancedFiltersLabel />}
      isPresent={isPresent}
      rootClassName={styles.filterPopupOverlay}
      open={open}
      onOpenChange={setOpen}
      placement="bottomLeft"
    >
      {popoverContent}
    </FilterPopup>
  );
}
